<template></template>

<script setup lang="ts">
import { useUiStore } from "@common/stores";
import { useNavigatorLanguage } from "@vueuse/core";
import { usePrimeVue } from "primevue/config";
import { onBeforeMount } from "vue";
import { setLocale } from "yup";
import * as yupLocales from "yup-locales";

// Variables
const { language } = useNavigatorLanguage();
const { theme } = useUiStore();

// Methods
const changeToEnglish = () => {
  const primeVue = usePrimeVue();
  const locale = primeVue.config.locale!;
  const aria = locale.aria!;

  locale.startsWith = "Starts with";
  locale.contains = "Contains";
  locale.notContains = "Not contains";
  locale.endsWith = "Ends with";
  locale.equals = "Equals";
  locale.notEquals = "Not equals";
  locale.noFilter = "No Filter";
  locale.lt = "Less than";
  locale.lte = "Less than or equal to";
  locale.gt = "Greater than";
  locale.gte = "Greater than or equal to";
  locale.dateIs = "Date is";
  locale.dateIsNot = "Date is not";
  locale.dateBefore = "Date is before";
  locale.dateAfter = "Date is after";
  locale.clear = "Clear";
  locale.apply = "Apply";
  locale.matchAll = "Match All";
  locale.matchAny = "Match Any";
  locale.addRule = "Add Rule";
  locale.removeRule = "Remove Rule";
  locale.accept = "Yes";
  locale.reject = "Yes";
  locale.choose = "Choose";
  locale.upload = "Upload";
  locale.cancel = "Cancel";
  locale.completed = "Completed";
  locale.pending = "Pending";
  locale.dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  locale.dayNamesShort = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  locale.dayNamesMin = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  locale.monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  locale.monthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  locale.chooseYear = "Choose Year";
  locale.chooseMonth = "Choose Month";
  locale.chooseDate = "Choose Date";
  locale.prevDecade = "Previous Decade";
  locale.nextDecade = "Next Decade";
  locale.prevYear = "Previous Year";
  locale.nextYear = "Next Year";
  locale.prevMonth = "Previous Month";
  locale.nextMonth = "Next Month";
  locale.prevHour = "Previous Hour";
  locale.nextHour = "Next Hour";
  locale.prevMinute = "Previous Minute";
  locale.nextMinute = "Next Minute";
  locale.prevSecond = "Previous Second";
  locale.nextSecond = "Next Second";
  locale.today = "Today";
  locale.weekHeader = "Wk";
  locale.dateFormat = "mm/dd/yy";
  locale.weak = "Weak";
  locale.medium = "Medium";
  locale.strong = "Strong";
  locale.passwordPrompt = "Enter a password";
  locale.searchMessage = "{0} results are available";
  locale.selectionMessage = "{0} items selected";
  locale.emptySelectionMessage = "No selected item";
  locale.emptySearchMessage = "No results found";
  locale.emptyMessage = "No available options";
  aria.trueLabel = "True";
  aria.falseLabel = "False";
  aria.nullLabel = "Not Selected";
  aria.star = "1 star";
  aria.stars = "{0} stars";
  aria.selectAll = "All items selected";
  aria.unselectAll = "All items unselected";
  aria.close = "Close";
  aria.previous = "Previous";
  aria.next = "Next";
  aria.navigation = "Navigation";
  aria.scrollTop = "Scroll Top";
  aria.moveTop = "Move Top";
  aria.moveUp = "Move Up";
  aria.moveDown = "Move Down";
  aria.moveBottom = "Move Bottom";
  aria.moveToTarget = "Move to Target";
  aria.moveToSource = "Move to Source";
  aria.moveAllToTarget = "Move All to Target";
  aria.moveAllToSource = "Move All to Source";
  aria.pageLabel = "Page {page}";
  aria.firstPageLabel = "First Page";
  aria.lastPageLabel = "Last Page";
  aria.nextPageLabel = "Next Page";
  aria.prevPageLabel = "Previous Page";
  aria.rowsPerPageLabel = "Rows per page";
  aria.jumpToPageDropdownLabel = "Jump to Page Dropdown";
  aria.jumpToPageInputLabel = "Jump to Page Input";
  aria.selectRow = "Row Selected";
  aria.unselectRow = "Row Unselected";
  aria.expandRow = "Row Expanded";
  aria.collapseRow = "Row Collapsed";
  aria.showFilterMenu = "Show Filter Menu";
  aria.hideFilterMenu = "Hide Filter Menu";
  aria.filterOperator = "Filter Operator";
  aria.filterConstraint = "Filter Constraint";
  aria.editRow = "Row Edit";
  aria.saveEdit = "Save Edit";
  aria.cancelEdit = "Cancel Edit";
  aria.listView = "List View";
  aria.gridView = "Grid View";
  aria.slide = "Slide";
  aria.zoomImage = "Zoom Image";
  aria.zoomIn = "Zoom In";
  aria.zoomOut = "Zoom Out";
  aria.rotateRight = "Rotate Right";
  aria.rotateLeft = "Rotate Left";
};

const changeToSpanish = () => {
  const primeVue = usePrimeVue();
  const locale = primeVue.config.locale!;
  const aria = locale.aria!;

  locale.startsWith = "Inicia con";
  locale.contains = "Contiene";
  locale.notContains = "No contiene";
  locale.endsWith = "Termina con";
  locale.equals = "Es igual a";
  locale.notEquals = "No es igual a";
  locale.noFilter = "Sin filtro";
  locale.lt = "Menor que";
  locale.lte = "Menor o Igual que";
  locale.gt = "Mayor que";
  locale.gte = "Mayor o Igual que";
  locale.dateIs = "Fecha es";
  locale.dateIsNot = "Fecha no es";
  locale.dateBefore = "Antes de";
  locale.dateAfter = "Después de";
  locale.clear = "Limpiar";
  locale.apply = "Aplicar";
  locale.matchAll = "Coincidir con todo";
  locale.matchAny = "Coincidir con alguno";
  locale.addRule = "Agregar Regla";
  locale.removeRule = "Eliminar Regla";
  locale.accept = "Si";
  locale.reject = "No";
  locale.choose = "Seleccionar";
  locale.upload = "Cargar";
  locale.cancel = "Cancelar";
  locale.completed = "Completado";
  locale.pending = "Pendiente";
  locale.dayNames = ["Domingo", "Lunes", "Martes", "Miercoles", "Jueves", "Viernes", "Sabado"];
  locale.dayNamesShort = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];
  locale.dayNamesMin = ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"];
  locale.monthNames = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  locale.monthNamesShort = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  locale.chooseYear = "Seleccionar Año";
  locale.chooseMonth = "Seleccionar Mes";
  locale.chooseDate = "Seleccionar Día";
  locale.prevDecade = "Década pasada";
  locale.nextDecade = "Siguiente Década";
  locale.prevYear = "Año anterior";
  locale.nextYear = "Siguiente Año";
  locale.prevMonth = "Mes anterior";
  locale.nextMonth = "Siguiente Mes";
  locale.prevHour = "Hora anterior";
  locale.nextHour = "Siguiente Hora";
  locale.prevMinute = "Minuto anterior";
  locale.nextMinute = "Siguiente Minuto";
  locale.prevSecond = "Segundo anterior";
  locale.nextSecond = "Siguiente Segundo";
  locale.today = "Hoy";
  locale.weekHeader = "Sem";
  locale.dateFormat = "dd/mm/yyyy";
  locale.weak = "Débil";
  locale.medium = "Medio";
  locale.strong = "Fuerte";
  locale.passwordPrompt = "Digite una contraseña";
  locale.searchMessage = "{0} resultados disponibles";
  locale.selectionMessage = "{0} items seleccionados";
  locale.emptySelectionMessage = "Ningún item seleccionado";
  locale.emptySearchMessage = "No se han encontrado resultados";
  locale.emptyMessage = "No hay opciones disponibles";
  aria.trueLabel = "Verdadero";
  aria.falseLabel = "Falso";
  aria.nullLabel = "No seleccionado";
  aria.star = "1 estrella";
  aria.stars = "{0} estrellas";
  aria.selectAll = "Todos los items seleccionados";
  aria.unselectAll = "Todos los items sin seleccionar";
  aria.close = "Cerrar";
  aria.previous = "Anterior";
  aria.next = "Siguiente";
  aria.navigation = "Navegación";
  aria.scrollTop = "Desplazarse al inicio";
  aria.moveTop = "Mover al inicio";
  aria.moveUp = "Mover hacia arriba";
  aria.moveDown = "Mover hacia abajo";
  aria.moveBottom = "Mover al final";
  aria.moveToTarget = "Mover al objetivo";
  aria.moveToSource = "Mover a la fuente";
  aria.moveAllToTarget = "Mover todo al objetivo";
  aria.moveAllToSource = "Mover todo a la fuente";
  aria.pageLabel = "Página {page}";
  aria.firstPageLabel = "Primera Página";
  aria.lastPageLabel = "Última Página";
  aria.nextPageLabel = "Siguiente Página";
  aria.prevPageLabel = "Página Anterior";
  aria.rowsPerPageLabel = "Filas por Página";
  aria.jumpToPageDropdownLabel = "Ir a la lista de páginas";
  aria.jumpToPageInputLabel = "Ir al campo de páginas";
  aria.selectRow = "Seleccionar fila";
  aria.unselectRow = "Deseleccionar fila";
  aria.expandRow = "Fila Expandida";
  aria.collapseRow = "Fila Colapsada";
  aria.showFilterMenu = "Mostrar filtro de Menú";
  aria.hideFilterMenu = "Ocultar filtro de Menú";
  aria.filterOperator = "Operador de filtro";
  aria.filterConstraint = "Restricción de filtro";
  aria.editRow = "Editar fila";
  aria.saveEdit = "Guardar";
  aria.cancelEdit = "Cancelar";
  aria.listView = "Vista de lista";
  aria.gridView = "Vista de grilla";
  aria.slide = "Deslizar";
  aria.zoomImage = "Ampliar imagen";
  aria.zoomIn = "Acercarse";
  aria.zoomOut = "Alejarse";
  aria.rotateRight = "Girar a la derecha";
  aria.rotateLeft = "Girar a la izquierda";
};

// Hooks
onBeforeMount(() => {
  const resolvedLanguage = language.value ?? "en";

  if (resolvedLanguage.startsWith("es")) {
    changeToSpanish();
    // biome-ignore lint/suspicious/noExplicitAny:
    setLocale((yupLocales as any).es);
  } else {
    changeToEnglish();
  }
});
</script>
